/**
 * Curated catalog for the worktop "Add Featured" chooser.
 *
 * This is intentionally a hand-maintained, marketing-shaped list — not a DB
 * query. It pairs each arche with positioning copy ("replaces X"), a
 * compare-with set, and a security note that can't be derived from the
 * `arches` table. Real icons are fetched at render time via
 * `api.getArcheDisplayMeta()`; the `icon` here is the lucide fallback shown
 * until/unless that icon loads (and the only icon for coming-soon entries).
 *
 * Keep `archId` values in sync with the seeded platform arches.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Image as ImageIcon,
  Calendar,
  Contact,
  ListTodo,
  Mail,
  MessagesSquare,
  Share2,
  HeartHandshake,
  TrendingUp,
  PenLine,
  FileText,
  Table,
  FileType,
  CalendarClock,
  SpellCheck,
  GitCompare,
  ChartPie,
  Languages,
  Calculator,
  Palette,
  Presentation,
  Gamepad2,
  MailPlus,
  UsersRound,
  Clapperboard,
  Send,
} from 'lucide-react';

/** A live, addable featured arche. */
export interface FeaturedArche {
  /** Arche id — used to fetch the real icon and to add to the worktop. */
  archId: string;
  name: string;
  /** What it does — one or two plain sentences. */
  description: string;
  /** Well-known products this replaces / compares with. */
  compareWith: string[];
  /** lucide fallback icon, shown until the real arche icon loads. */
  icon: LucideIcon;
  /** Arche-specific addendum to the shared Vault security explainer. */
  securityNote: string;
}

/** A not-yet-shipped category teased in the "Coming soon" section. */
export interface ComingSoonArche {
  name: string;
  /** Describes the category. */
  description: string;
  /** Several applications this category will stand in for. */
  compareWith: string[];
  icon: LucideIcon;
}

/**
 * Available now — first-party arches that replace common subscriptions.
 * Order is the display order in the chooser.
 */
export const FEATURED_ARCHES: FeaturedArche[] = [
  {
    archId: 'arche_image_editor',
    name: 'Image Editor',
    description:
      'Edit images right in your browser — crop, retouch, filters, and AI-powered edits like background removal and generative fill.',
    compareWith: ['Photoshop', 'Lightroom', 'Pixlr'],
    icon: ImageIcon,
    securityNote:
      'Images you edit are stored as encrypted Vault documents on your Personal Holder — they stay yours and are never added to a shared training set.',
  },
  {
    archId: 'arche_calendar',
    name: 'Calendar',
    description:
      'Schedule events, manage your day, and get reminders in a fast, keyboard-friendly calendar.',
    compareWith: ['Google Calendar', 'Outlook Calendar', 'Fantastical'],
    icon: Calendar,
    securityNote:
      'Your events are Holder-scoped Vault records — visible only to you unless you explicitly share a calendar.',
  },
  {
    archId: 'arche_contact_book',
    name: 'Contact Book',
    description:
      'Keep every contact in one place, with smart matching and de-duplication so records never drift apart.',
    compareWith: ['Google Contacts', 'Apple Contacts', 'Cardhop'],
    icon: Contact,
    securityNote:
      'Contacts live in your private Vault, scoped to you — no other user or arche can read them without your consent.',
  },
  {
    archId: 'arche_todo_lists',
    name: 'Todo Lists',
    description:
      'Organize tasks, projects, and sprints — from a simple checklist all the way to full issue tracking. Replaces Jira.',
    compareWith: ['Jira', 'Asana', 'Trello', 'Linear'],
    icon: ListTodo,
    securityNote:
      'Tasks and boards are Holder-scoped Vault data; team boards are shared only with the people you add, every change audited.',
  },
  {
    archId: 'arche_email',
    name: 'Email',
    description:
      'Full-featured email with AI-powered inbox management — sender screening, snooze, scheduled send, templates, and threading.',
    compareWith: ['Gmail', 'Outlook', 'HEY', 'Superhuman'],
    icon: Mail,
    securityNote:
      'Your mailbox credentials are encrypted at rest under a dedicated key, and messages and attachments are Holder-scoped Vault records — no other user or arche can read them without your consent.',
  },
  {
    archId: 'arche_chat',
    name: 'Chat & Guilds',
    description:
      'Real-time chat, direct messages, and Guild communities with channels and @-mentions. Replaces Slack.',
    compareWith: ['Slack', 'Discord', 'Microsoft Teams'],
    icon: MessagesSquare,
    securityNote:
      'Messages stay within your Guilds and DMs — access is membership-gated, so only people in a channel can read it.',
  },
  {
    archId: 'arche_social_media',
    name: 'Social Media',
    description:
      'Compose, schedule, and publish posts across X, LinkedIn, Facebook, and Instagram from one place. Replaces Buffer.',
    compareWith: ['Buffer', 'Hootsuite', 'Later', 'Sprout Social'],
    icon: Share2,
    securityNote:
      'Your connected-account tokens are encrypted per user; drafts and scheduled posts are Holder-scoped Vault records.',
  },
  {
    archId: 'arche_fias360',
    name: 'Fias 360',
    description:
      'Manage post-sale customer relationships, renewals, and account health in a single view. Replaces Customer 360.',
    compareWith: ['Salesforce Customer 360', 'Gainsight', 'HubSpot Service Hub'],
    icon: HeartHandshake,
    securityNote:
      'Customer records are scoped to your organization Holder; only teammates you grant access to can see them, every read and write audited.',
  },
  {
    archId: 'arche_fias_leads',
    name: 'Fias Leads',
    description:
      'Track leads, opportunities, and your sales pipeline from first touch to close. Replaces Salesforce Sales Cloud.',
    compareWith: ['Salesforce Sales Cloud', 'HubSpot CRM', 'Pipedrive', 'Zoho CRM'],
    icon: TrendingUp,
    securityNote:
      'Your pipeline lives in your organization Vault — isolated from every other tenant and never shared without an explicit grant.',
  },
  {
    archId: 'arche_document_sign',
    name: 'Document Sign',
    description:
      'Send documents for legally-binding e-signature with audit trails, reminders, and a tamper-evident record. Replaces DocuSign.',
    compareWith: ['DocuSign', 'Adobe Acrobat Sign', 'Dropbox Sign', 'PandaDoc'],
    icon: PenLine,
    securityNote:
      'Signed documents and their audit trail are stored as encrypted Vault records; sharing for signature is a per-document consent grant.',
  },
];

/**
 * Coming soon — categories we're building next. No arche ids yet; each entry
 * describes the category and the incumbents it will replace.
 */
export const COMING_SOON_ARCHES: ComingSoonArche[] = [
  {
    name: 'Documents',
    description: 'Write and collaborate on rich-text documents.',
    compareWith: ['Microsoft Word', 'Google Docs', 'Pages', 'Notion'],
    icon: FileText,
  },
  {
    name: 'Spreadsheets',
    description: 'Crunch numbers, build models, and visualize data in spreadsheets.',
    compareWith: ['Microsoft Excel', 'Google Sheets', 'Numbers', 'Airtable'],
    icon: Table,
  },
  {
    name: 'PDF Tools',
    description: 'View, edit, annotate, fill, and combine PDF files.',
    compareWith: ['Adobe Acrobat', 'PDF Expert', 'Foxit', 'Smallpdf'],
    icon: FileType,
  },
  {
    name: 'Scheduling',
    description: 'Share booking links and let others grab time on your calendar.',
    compareWith: ['Calendly', 'Cal.com', 'Acuity Scheduling', 'Doodle'],
    icon: CalendarClock,
  },
  {
    name: 'Writing Assistant',
    description: 'AI grammar, spelling, clarity, and tone suggestions as you write.',
    compareWith: ['Grammarly', 'ProWritingAid', 'Hemingway', 'LanguageTool'],
    icon: SpellCheck,
  },
  {
    name: 'Document Compare',
    description: 'Redline and compare versions to catch every change between drafts.',
    compareWith: ['Litera Compare', 'Draftable', 'Word Compare', 'iManage'],
    icon: GitCompare,
  },
  {
    name: 'Cap Table',
    description: 'Manage equity, cap tables, and ownership for your company.',
    compareWith: ['Carta', 'Pulley', 'Shareworks', 'Ledgy'],
    icon: ChartPie,
  },
  {
    name: 'Translation',
    description: 'Translate text and whole documents across languages instantly.',
    compareWith: ['Google Translate', 'DeepL', 'Microsoft Translator'],
    icon: Languages,
  },
  {
    name: 'Accounting',
    description: 'Bookkeeping, invoicing, and financial reporting for your business.',
    compareWith: ['QuickBooks', 'Xero', 'FreshBooks', 'Wave'],
    icon: Calculator,
  },
  {
    name: 'Design Studio',
    description: 'Create graphics, social posts, and marketing designs from templates.',
    compareWith: ['Canva', 'Adobe Express', 'Figma', 'PicMonkey'],
    icon: Palette,
  },
  {
    name: 'Presentations',
    description: 'Build and deliver slide decks with themes, charts, and animations.',
    compareWith: ['PowerPoint', 'Google Slides', 'Keynote', 'Pitch'],
    icon: Presentation,
  },
  {
    name: 'Game Ecosystem',
    description: 'Build, share, and play user-created games and worlds.',
    compareWith: ['Roblox', 'Fortnite Creative', 'Minecraft', 'Core'],
    icon: Gamepad2,
  },
  {
    name: 'Email Automation',
    description: 'Build email campaigns, automations, and audience segments.',
    compareWith: ['Mailchimp', 'Klaviyo', 'HubSpot', 'ConvertKit'],
    icon: MailPlus,
  },
  {
    name: 'HR Software',
    description: 'Manage people, payroll, time off, and onboarding.',
    compareWith: ['Workday', 'BambooHR', 'Gusto', 'Rippling'],
    icon: UsersRound,
  },
  {
    name: 'Video Editing',
    description: 'Cut, trim, and produce videos with transitions and effects.',
    compareWith: ['Premiere Pro', 'Final Cut Pro', 'DaVinci Resolve', 'CapCut'],
    icon: Clapperboard,
  },
  {
    name: 'File Transfers',
    description: 'Send large files securely with shareable, expiring links.',
    compareWith: ['WeTransfer', 'Dropbox Transfer', 'Smash', 'Send Anywhere'],
    icon: Send,
  },
];

/** All arche ids referenced by the available catalog — fed to the icon fetch. */
export const FEATURED_ARCHE_IDS = FEATURED_ARCHES.map((a) => a.archId);
